import {apiRequest} from "./client";
import type {
  AccountNetwork,
  AccountOrder,
  AccountProfile,
  AccountReturn,
  CaptchaChallenge,
  County,
  InfluencerDashboard,
  Product,
  SavedAddress,
  SavedCompany
} from "./types";

export async function getAccountProfile(): Promise<AccountProfile> {
  return apiRequest<AccountProfile>("/account/profile");
}

export async function updateAccountProfile(data: {nume: string; email: string; telefon: string}): Promise<{ok: boolean}> {
  return apiRequest<{ok: boolean}>("/account/profile", {method: "PATCH", body: data});
}

export async function changeAccountPassword(data: {parola1: string; parola2: string}): Promise<{ok: boolean}> {
  return apiRequest<{ok: boolean}>("/account/password", {method: "POST", body: data});
}

export async function getAccountAddresses(): Promise<{items: SavedAddress[]}> {
  return apiRequest<{items: SavedAddress[]}>("/account/addresses");
}

export async function addAccountAddress(data: Omit<SavedAddress, "id">): Promise<{items: SavedAddress[]}> {
  return apiRequest<{items: SavedAddress[]}>("/account/addresses", {method: "POST", body: data});
}

export async function updateAccountAddress(id: number, data: Omit<SavedAddress, "id">): Promise<{items: SavedAddress[]}> {
  return apiRequest<{items: SavedAddress[]}>(`/account/addresses/${id}`, {method: "PATCH", body: data});
}

export async function deleteAccountAddress(id: number): Promise<{items: SavedAddress[]}> {
  return apiRequest<{items: SavedAddress[]}>(`/account/addresses/${id}`, {method: "DELETE"});
}

export async function getAccountOrders(): Promise<{items: AccountOrder[]}> {
  return apiRequest<{items: AccountOrder[]}>("/account/orders");
}

export async function getAccountOrder(id: number): Promise<AccountOrder> {
  return apiRequest<AccountOrder>(`/account/orders/${id}`);
}

export async function getAccountReturns(): Promise<{items: AccountReturn[]}> {
  return apiRequest<{items: AccountReturn[]}>("/account/returns");
}

export async function getAccountNetwork(): Promise<AccountNetwork> {
  return apiRequest<AccountNetwork>("/account/network");
}

export async function generateNetworkVoucher(amount: number): Promise<{ok: boolean}> {
  return apiRequest<{ok: boolean}>("/account/network/voucher", {method: "POST", body: {pct: amount}});
}

export async function refreshNetworkCode(): Promise<{inviteCode: string}> {
  return apiRequest<{inviteCode: string}>("/account/network/refresh-code", {method: "POST"});
}

export async function getAccountNotifications(): Promise<{items: Product[]}> {
  return apiRequest<{items: Product[]}>("/account/notifications");
}

export async function getAccountCounties(): Promise<{items: County[]}> {
  return apiRequest<{items: County[]}>("/account/counties");
}

export async function getAccountCompanies(): Promise<{items: SavedCompany[]}> {
  return apiRequest<{items: SavedCompany[]}>("/account/companies");
}

export async function getAccountCompany(id: number): Promise<SavedCompany> {
  return apiRequest<SavedCompany>(`/account/companies/${id}`);
}

export async function addAccountCompany(data: Omit<SavedCompany, "id">): Promise<{items: SavedCompany[]}> {
  return apiRequest<{items: SavedCompany[]}>("/account/companies", {method: "POST", body: data});
}

export async function updateAccountCompany(id: number, data: Omit<SavedCompany, "id">): Promise<{items: SavedCompany[]}> {
  return apiRequest<{items: SavedCompany[]}>(`/account/companies/${id}`, {method: "PATCH", body: data});
}

export async function deleteAccountCompany(id: number): Promise<{items: SavedCompany[]}> {
  return apiRequest<{items: SavedCompany[]}>(`/account/companies/${id}`, {method: "DELETE"});
}

export async function deleteNetworkMember(id: number): Promise<AccountNetwork> {
  return apiRequest<AccountNetwork>(`/account/network/members/${id}`, {method: "DELETE"});
}

export async function getAccountInfluencer(): Promise<InfluencerDashboard> {
  return apiRequest<InfluencerDashboard>("/account/influencer");
}

export async function createInfluencerVoucher(data: {
  validDays: number;
  scopes: Array<{type: string; id: number}>;
}): Promise<InfluencerDashboard> {
  return apiRequest<InfluencerDashboard>("/account/influencer/vouchers", {method: "POST", body: data});
}

export async function requestInfluencerPayout(paymentDetails: string): Promise<InfluencerDashboard> {
  return apiRequest<InfluencerDashboard>("/account/influencer/payouts", {
    method: "POST",
    body: {paymentDetails}
  });
}

export async function getCaptcha(): Promise<CaptchaChallenge> {
  return apiRequest<CaptchaChallenge>("/auth/captcha");
}

export async function passwordReset(data: {email: string; cod: number}): Promise<{ok: boolean; message?: string}> {
  return apiRequest<{ok: boolean; message?: string}>("/auth/password-reset", {method: "POST", body: data});
}

export type {AccountOrder, AccountProfile, AccountReturn, AccountNetwork, SavedCompany, InfluencerDashboard};
